/**
 * Shared delete-confirmation gate for any surface with a destructive row
 * action. Confirms with the user before invoking the caller-supplied
 * mutation, so every delete affordance in the app shows the same "are you
 * sure" dialog instead of each screen hand-rolling its own. The hook never
 * performs the mutation itself — confirmDelete's onConfirm callback owns the
 * actual API call/dispatch, error reporting, and any post-delete refresh;
 * the hook only gates the click and toggles a `submitting` flag around the
 * awaited call.
 *
 * Render the returned dialogElement once, near the top of the consuming
 * component's JSX tree — it renders null while no delete is pending.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DeleteConfirmDialog from '@sophocles/components/ConfirmationDialog/DeleteConfirmDialog';
import { buildPendingDelete } from '@sophocles/hooks/deleteConfirmState';

/**
 * @returns {{
 *   confirmDelete: (row: unknown, options: { entityLabel: string, body?: import('react').ReactNode, onConfirm: (row: unknown) => (Promise<unknown>|unknown) }) => void,
 *   dialogElement: (JSX.Element|null),
 * }}
 */
export default function useDeleteConfirm() {
  const [pending, setPending] = useState(null); // { row, entityLabel, onConfirm } | null
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const confirmDelete = useCallback((row, options) => {
    setPending(buildPendingDelete(row, options));
  }, []);

  const closeDialog = useCallback(() => {
    setPending(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      await pending.onConfirm(pending.row);
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
        setPending(null);
      }
    }
  }, [pending]);

  const dialogElement = pending ? (
    <DeleteConfirmDialog
      open
      entityLabel={pending.entityLabel}
      body={pending.body}
      submitting={submitting}
      onConfirm={handleConfirm}
      onCancel={closeDialog}
    />
  ) : null;

  return useMemo(() => ({ confirmDelete, dialogElement }), [confirmDelete, dialogElement]);
}
